import editorialStyle from "../../../components/editorial/style.scss";
import expandableStyle from "../../../components/expandable/style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  ButtonBar,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Tags from "../../../components/tags";
import Image from "../../../components/image";
import ImageSet from "../../../components/image-set";
import Author from "../../../components/author";
import NoWrap from "../../../components/no-wrap";
import Expandable from "../../../components/expandable";
import Accordion from "../../../components/accordion";
import AccordionPreview from "../../../components/accordion/preview";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import { lookupByUrl } from "../../../settings/route-data-lookup";
import { mergeClasses } from "../../../components/helpers";
import namedRoutes from "../../../settings/routes";

import powerStationData from "../../../data/power-stations/jackery";
import { getGroupedData } from "../../power-systems/dataHelper";
import DataView from "../../power-systems/dataView";

const highlightedProducts = [
  "Explorer 1000 Plus",
  "Explorer 1500 Ultra",
  "Explorer 2000 v2",
  "Explorer 2000 Plus",
  "HomePower 3000",
];
const groupedData = getGroupedData(powerStationData, {
  overrideProp: "highlighted",
  overrides: highlightedProducts,

  getGroupName: (item) => {
    return "allSystems";
  },
  groups: ["allSystems"],
});

const headerInfo = lookupByUrl(namedRoutes.jackery.vanlife);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionY={50}
      background="/images/backgrounds/jackery.webp"
      titlePrefix="The Ultimate Guide to "
      title="Jackery Power Systems"
      subTitle="For Vanlife, RVs & Overlanding"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          <strong>
            Jackery is one of the most recognizable names in portable power and
            solar generators.
          </strong>{" "}
          Their power stations are known for being rugged, simple, and
          plug‑and‑play, making them popular with campers, weekend travelers,
          and first‑time vanlife builders. For dedicated vanlife or RV
          electrical systems, though, Jackery has several technical limitations
          you should understand before choosing one. The biggest constraint is
          their restricted 12V DC output, which affects how well they integrate
          with a full van build.
        </p>

        <AffiliateDisclosure />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Power Stations</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Top Models Comparison</SlugLink>
                      </li>
                      <li>
                        <SlugLink>All Power Systems</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Limited DC Output</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Alternator Chargers</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Solar Power</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Shore Power</SlugLink>
                  </li>
                </ol>
              ),
            },
            {
              heading: "Terminology",
              content: (
                <Fragment>
                  <p>
                    <strong>AC power</strong> (alternating current) is the type
                    of electricity found in household outlets, allowing you to
                    run standard appliances such as a toaster, induction
                    cooktop, computer, etc.
                  </p>
                  <p>
                    <strong>Alternator</strong> is a device that powers a
                    vehicle’s electrical systems and charges the battery while
                    the engine runs.
                  </p>
                  <p>
                    <strong>Amperage</strong> (Amps, A) measures the rate of
                    electric current flow.
                  </p>
                  <p>
                    <strong>DC power</strong> (direct current) is the
                    electricity that your vehicle, Jackery devices, and most of
                    your essential accessories like fridges, fans, heaters, and
                    lights will use.
                  </p>
                  <p>
                    <strong>Solar array</strong> is a group of solar panels that
                    work together to convert sunlight into electricity.
                  </p>
                  <p>
                    <strong>Volts</strong> (V) measure the electric potential
                    difference between two points, indicating how strongly
                    electricity is pushed through a circuit.
                  </p>
                  <p>
                    <strong>Watts</strong> (W) are calculated by multiplying
                    volts by amps.
                  </p>
                  <p>
                    <strong>Watt-hours</strong> (Wh) and{" "}
                    <strong>kilowatt-hours</strong> (kWh) measure total energy
                    used or produced over time.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Power Stations</SlugHeading>
        <p>
          Jackery power stations provide a straightforward alternative to DIY
          electrical systems, with solid AC performance but limited 12V output
          for vanlife and RV builds. These are the five best Jackery models to
          consider for off‑grid travel.
        </p>

        <ImageSet
          size={5}
          images={[
            {
              lg: "/images/diagrams/power-systems/jackery/jackery-explorer-1000-plus.webp?v=2",
              alt: "Jackery Explorer 1000 Plus vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/jackery/jackery-explorer-1500-ultra.webp?v=2",
              alt: "Jackery Explorer 1500 Ultra vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/jackery/jackery-explorer-2000-v2.webp?v=2",
              alt: "Jackery Explorer 2000 v2 vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/jackery/jackery-explorer-2000-plus.webp?v=2",
              alt: "Jackery Explorer 2000 Plus vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/jackery/jackery-homepower-3000.webp?v=2",
              alt: "Jackery HomePower 3000 vanlife system diagram",
            },
          ]}
        />
        <br />

        <SlugHeading tag="h3">Top Models Comparison</SlugHeading>
        <p>
          A side‑by‑side comparison of five top Jackery systems, sorted by
          battery capacity:
        </p>
        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky}>Model</th>
                <th>Explorer 1000 Plus</th>
                <th>Explorer 1500 Ultra</th>
                <th>Explorer 2000 v2</th>
                <th>Explorer 2000 Plus</th>
                <th>HomePower 3000</th>
              </tr>
            </thead>
            <tbody>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Photo</strong>
                </th>
                <td>
                  <Image
                    src="/images/products/jackery/explorer-1000-plus/front.webp"
                    alt="Jackery Explorer 1000 Plus"
                    showAlt
                    maxHeight={100}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/jackery/explorer-1500-ultra/front.webp"
                    alt="Jackery Explorer 1500 Ultra"
                    showAlt
                    maxHeight={100}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/jackery/explorer-2000-v2/front.webp"
                    alt="Jackery Explorer 2000 v2"
                    showAlt
                    maxHeight={100}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/jackery/explorer-2000-plus/front.webp"
                    alt="Jackery Explorer 2000 Plus"
                    showAlt
                    maxHeight={100}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/jackery/homepower-3000/front.webp"
                    alt="Jackery HomePower 3000"
                    showAlt
                    maxHeight={100}
                  />
                </td>
              </tr>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Links</strong>
                </th>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.jackery}
                      href="https://www.jackery.com/products/jackery-explorer-1000-plus-portable-power-station?variant=40233095135319"
                      icon="Jackery"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://r.jackery.net/productGuide/Jackery%20Explorer%201000%20Plus%20User%20Manual.pdf"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.jackery}
                      href="https://www.jackery.com/products/jackery-explorer-1500-ultra-portable-power-station?variant=42447145435223"
                      icon="Jackery"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://cdn.shopify.com/s/files/1/0970/9262/files/Jackery_Explorer_1500_Ultra_USER_MANUAL.pdf?v=1766979182"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.jackery}
                      href="https://www.jackery.com/products/jackery-explorer-2000-v2-portable-power-station?variant=41264600842327"
                      icon="Jackery"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://r.jackery.net/productGuide/Jackery%20Explorer%202000%20v2%20User%20Manual.pdf"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.jackery}
                      href="https://www.jackery.com/products/explorer-2000-plus-portable-power-station?variant=40192995197015"
                      icon="Jackery"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://r.jackery.net/productGuide/Jackery%20Explorer%202000%20Plus%20User%20Manual.pdf"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.jackery}
                      href="https://www.jackery.com/products/jackery-homepower-3000?variant=41685844983895"
                      icon="Jackery"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://cdn.shopify.com/s/files/1/0970/9262/files/Jackery_HomePower_3000_User_Manual_d7e442c7-e1aa-4845-910b-8d6f7f3a793f.pdf?v=1748426235"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Price</strong>
                </th>
                <td>
                  <NoWrap>$999</NoWrap>
                </td>
                <td>
                  <NoWrap>
                    $999 <s>$1,299</s>
                  </NoWrap>
                </td>
                <td>
                  <NoWrap>
                    $799 <s>$1,499</s>
                  </NoWrap>
                </td>
                <td>
                  <NoWrap>
                    $1,099 <s>$2,199</s>
                  </NoWrap>
                </td>
                <td>
                  <NoWrap>
                    $1,299 <s>$2,499</s>
                  </NoWrap>
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Capacity</strong>
                </th>
                <td>1,264Wh</td>
                <td>1,536Wh</td>
                <td>2,042Wh</td>
                <td>2,042.8Wh</td>
                <td>3,072Wh</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Expansion Battery</strong>
                </th>
                <td>3x</td>
                <td>--</td>
                <td>--</td>
                <td>5x</td>
                <td>--</td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>AC output</strong>
                </th>
                <td>
                  2,000W total <br />
                  4,000W surge
                </td>
                <td>
                  1,800W total <br />
                  3,600W surge
                </td>
                <td>
                  2,200W total <br />
                  4,800W surge
                </td>
                <td>
                  3,000W total <br />
                  6,000W surge
                </td>
                <td>
                  3,600W total <br />
                  7,200W surge
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>AC ports</strong>
                </th>
                <td>
                  x3 (16.6A each) <br />
                </td>
                <td>
                  x3 (12A each) <br />
                </td>
                <td>
                  x3 (18.3A each) <br />
                </td>
                <td>
                  x4 (20A each) <br />
                  x1 (25A) <br />
                </td>
                <td>
                  x4 (20A each) <br />
                  x1 (30A) <br />
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>DC output</strong>
                </th>
                <td>12V⎓10A</td>
                <td>12V⎓10A</td>
                <td>12V⎓10A</td>
                <td>12V⎓10A</td>
                <td>12V⎓10A</td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Solar input</strong>
                </th>
                <td>
                  16-60V⎓12A <br />
                  800W <sup>1</sup>
                </td>
                <td>
                  16-60V⎓12A <br />
                  800W <sup>1</sup>
                </td>
                <td>
                  16-60V⎓12A <br />
                  400W <sup>1</sup>
                </td>
                <td>
                  17.5-60V⎓12A <br />
                  1,400W <sup>1</sup>
                </td>
                <td>
                  16-60V⎓12A <br />
                  1,000W <sup>1</sup>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>AC input</strong>
                </th>
                <td>15A 1,800W</td>
                <td>15A 1,800W</td>
                <td>15A 1,800W</td>
                <td>15A 1,800W</td>
                <td>15A 1,800W</td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>USB A</strong>
                </th>
                <td>2x</td>
                <td>1x</td>
                <td>1x</td>
                <td>2x</td>
                <td>2x</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>USB C</strong>
                </th>
                <td>2x</td>
                <td>2x</td>
                <td>2x</td>
                <td>2x</td>
                <td>2x</td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Size (in)</strong>
                </th>
                <td>14 × 10.2 × 11.1</td>
                <td>13.2 × 10.4 × 11.6</td>
                <td>13.2 × 10.4 × 11.5</td>
                <td>18.6 × 14.1 × 14.7</td>
                <td>16.4 × 12.8 × 12</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Size (mm)</strong>
                </th>
                <td>
                  <NoWrap>356 × 260 × 283</NoWrap>
                </td>
                <td>
                  <NoWrap>336 × 264 × 295</NoWrap>
                </td>
                <td>
                  <NoWrap>335 × 264 × 292</NoWrap>
                </td>
                <td>
                  <NoWrap>473 × 359 × 374</NoWrap>
                </td>
                <td>
                  <NoWrap>416 × 325 × 305</NoWrap>
                </td>
              </tr>
            </tbody>
          </table>
        </FixedTable>
        <p class="caption">
          <strong>1.</strong> Jackery two ports combined total.
        </p>
        <br />

        <SlugHeading tag="h3">All Power Systems</SlugHeading>
        <DataView data={groupedData.allSystems} />

        <p>
          <strong>See how other power systems compare:</strong>
        </p>
        <ReviewItem href={namedRoutes.powerSystems} />
        <br />

        <h3>Limited DC Output</h3>
        <p>
          The most significant hurdle for using a Jackery in a van or RV is the
          limited 12V DC output. While several EcoFlow and BLUETTI models offer
          a 30A DC output to run a full DC fuse block, current Jackery units are
          capped at only 10A total DC output via a single cigarette lighter
          socket.
        </p>

        <h4>Why this matters for your build:</h4>
        <ul>
          <li>
            <strong>Limited Simultaneous Use:</strong> A typical 12V fridge
            pulls 6-10A. If you add a MaxxAir fan (~4A) and LED lights (~1A),
            you are above the 10A limit.
          </li>
          <li>
            <strong>Inductive Loads:</strong> Items like diesel/gasoline heaters
            or water pumps have high startup surges (sometimes over 12A). These
            will often trip the Jackery’s DC circuit breaker instantly.
          </li>
          <li>
            <strong>Workaround:</strong> To run a real 12V system, you would
            have to use the Jackery’s AC inverter to run a separate 12V power
            supply (such as a{" "}
            <Link
              affiliate="https://amzn.to/4tKei98"
              href="https://www.amazon.com/dp/B08NY23BKF"
            >
              Victron Blue Smart IP22 Charger
            </Link>
            ). This is an inefficient process that wastes battery capacity
            running the inverter and in conversion loss.
          </li>
        </ul>

        <p>
          <strong>Need higher DC output?</strong> Check out other brands that
          support 30A DC or higher:
        </p>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.vanlife} />
          </li>
        </ul>
        <br />

        <h3>Battery Chemistry</h3>
        <p>
          Not all lithium batteries are equal. You may still see older
          discounted models using outdated lithium‑ion chemistry, especially on
          third‑party sites. Some shoppers choose older models because of the
          lower price without realizing the difference in cycle life.
        </p>
        <ul>
          <li>
            <strong>LiFePO₄ / LFP / Lithium Iron Phosphate:</strong> Jackery
            rates these for 4,000 cycles to 70% capacity. Used in all new
            Jackery models.
          </li>
          <li>
            <strong>Lithium-ion (NMC):</strong> Rated for &lt;1,000 cycles.
            Found in older models.
          </li>
        </ul>
        <br />

        <h3>What Jackery does well</h3>
        <ul>
          <li>Popular well‑known brand</li>
          <li>Simple, plug‑and‑play operation</li>
          <li>Reliable LiFePO₄ batteries in new models</li>
          <li>Great for weekenders or simple builds</li>
        </ul>
        <br />
      </Center>
      <Center>
        <SlugHeading tag="h2">Alternator Chargers</SlugHeading>
        <p>
          Alternator charging is essential for vanlife, offering fast, reliable
          recharging while driving. It often outperforms solar and isn’t
          dependent on sunshine or good weather. Jackery currently offers a 600W
          alternator charger model. It includes an ACC signal wire, making it
          more reliable for modern vehicles with smart alternators.
        </p>

        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky}>Model</th>
                <th>DC-DC Alternator Charger</th>
              </tr>
            </thead>
            <tbody>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Photo</strong>
                </th>
                <td>
                  <Image
                    src="/images/products/jackery/jackery-dc-dc-alternator-charger.webp"
                    alt="DC-DC Alternator Charger"
                    showAlt
                    maxHeight={100}
                  />
                </td>
              </tr>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Links</strong>
                </th>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.jackery}
                      href="https://www.jackery.com/products/jackery-dc-dc-alternator-charger"
                      icon="Jackery"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://r.jackery.net/productGuide/Jackery%20DC-DC%20Charger%20User%20Manual.pdf"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Price</strong>
                </th>
                <td>
                  <NoWrap>
                    $244 <s>$329</s>
                  </NoWrap>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Charger Output</strong>
                </th>
                <td>600W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>ACC Signal Wire</strong>
                </th>
                <td>Included</td>
              </tr>
            </tbody>
          </table>
        </FixedTable>

        <p>
          The Jackery alternator charger uses a DC8020 connector that is
          intended to be used with Jackery power stations.
        </p>
        <p>
          <strong>Not sure it’s the best fit?</strong> My{" "}
          <Link href={namedRoutes.alternatorChargers}>
            alternator charger guide
          </Link>{" "}
          covers all the popular brands.
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Solar Power</SlugHeading>
        <p>
          Solar charging offers a quiet, sustainable, and free way to extend
          off-grid time. However, its effectiveness depends entirely on
          sunlight; factors like shade, sun angle, temperature, and wiring
          configuration (series, parallel, or series-parallel) all significantly
          impact power output.
        </p>

        <SlugHeading tag="h3">Solar Array Configuration</SlugHeading>
        <p>
          Solar panel setups often involve arrays of multiple panels using
          series, parallel, or series-parallel wiring. Series connections add
          voltage but can suffer significant power drops if even one panel is
          partially shaded. Parallel wiring adds current (amperage), allowing
          unshaded panels to still produce power, ideal for patchy sunlight.
          Series-parallel setups combine both approaches.
        </p>
        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Calculating Solar Array Output",
              content: (
                <Fragment>
                  <p>
                    To calculate the total voltage and amperage of your solar
                    array, you'll use the Open Circuit Voltage (Voc) and Short
                    Circuit Current (Isc) listed on your panels.
                  </p>
                  <p>For panels wired in series:</p>
                  <p style="padding:0.6em 1em; background: #ddd; border-radius: 6px;">
                    Total Array Voltage = Voc_panel1 + Voc_panel2 + ...
                    <br />
                    Total Array Amperage = Isc_of_a_single_panel
                  </p>
                  <p>For panels wired in parallel:</p>
                  <p style="padding:0.6em 1em; background: #ddd; border-radius: 6px;">
                    Total Array Voltage = Voc_of_a_single_panel
                    <br />
                    Total Array Amperage = Isc_panel1 + Isc_panel2 + ...
                  </p>
                  <p>
                    In a series-parallel setup, you'll first calculate the
                    voltage of each series string, then add the current of those
                    strings together if they're connected in parallel.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />

        <SlugHeading tag="h3">Solar Panel Types</SlugHeading>
        <p>
          Jackery offers{" "}
          <Link
            affiliateData={affiliateMerchants.jackery}
            href="https://www.jackery.com/collections/solar-panels"
          >
            portable solar panels
          </Link>
          , but you may prefer rigid panels for a permanent roof mount.
        </p>

        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Types of Solar Panels",
              content: (
                <Fragment>
                  <p>
                    <strong>Mountable vs portable:</strong> Mountable solar
                    panels provide a semi-permanent and typically higher-output
                    charging solution affixed to the roof, whereas portable
                    solar panels offer the flexibility to chase the sun and can
                    be stowed away when not in use or during travel.
                  </p>
                  <p>
                    <strong>Rigid vs flexible:</strong> Rigid solar panels, with
                    their thicker framed design, offer greater durability and
                    efficiency and are common for roof installations. Flexible
                    panels are much thinner, lighter‑weight, and can conform to
                    a curved surface for stealthier installations.
                  </p>
                  <p>
                    <strong>Bifacial vs monofacial:</strong> Bifacial solar
                    panels gather light from both sides for potentially higher
                    energy yield than traditional monofacial panels.
                  </p>
                  <p>
                    <strong>Monocrystalline vs polycrystalline:</strong>{" "}
                    Monocrystalline panels are more efficient and therefore
                    space-saving, ideal for limited RV/van roof space, while
                    older polycrystalline panels can be less expensive but
                    aren’t recommended.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />

        <p>
          While Jackery provides various solar panel options and bundles,
          prioritize panels that physically fit your vehicle; using other
          reputable brands is okay. I personally use{" "}
          <Link external href="https://www.newpowa.com/">
            Newpowa
          </Link>{" "}
          panels as they fit my rack setup best.
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Shore Power</SlugHeading>
        <p>
          Shore power charging allows you to replenish your Jackery power
          station by plugging into a standard electrical outlet, often found at
          campgrounds or homes, using the power station's normal AC charging
          cable. For easier charging, you can install a weather-resistant AC
          port on your vehicle, such as the{" "}
          <Link
            affiliate="https://amzn.to/4juX4cb"
            href="https://www.amazon.com/dp/B009ANV81S"
          >
            Noco GCP1
          </Link>
          .
        </p>
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
    </Editorial>
  </div>
);

export default Page;
